import { noCase } from "change-case";
import { useEffect, useState } from "react";
// @mui
import {
  Box,
  Stack,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  IconButton,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
} from "@mui/material";
// utils
import { fToNow } from "../../../utils/formatTime";
// _mock_
import { _notifications } from "../../../_mock/arrays";
// components
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import MenuPopover from "../../../components/menu-popover";
import { IconButtonAnimate } from "../../../components/animate";
import useService, { QueryKeys } from "src/hooks/useService";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const selectorAuth = useSelector((state: RootState) => state.auth.auth);
  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const [notifications, setNotifications] = useState<Array<any>>([]);
  const [totalUnRead, setTotalUnRead] = useState<number>(0);

  const {
    data: notificationsData,
    error: errorGetNotification,
    loading: notificationsMutate,
    refetch: triggerGetNotifications,
  } = useService(
    `/notification/by_user/${selectorAuth?.userReturn?.id || ""}`,
    {
      key: QueryKeys.NOTIFICATION_HOME,
      cacheMinutes: 5,
      manual: true,
    }
  );

  const { put: triggerPutNotifications } = useService(
    `/notification/${selectorAuth?.userReturn?.id}`,
    { manual: true }
  );

  useEffect(() => {
    triggerGetNotifications();
  }, []);

  useEffect(() => {
    if (notificationsData) {
      let arr: Array<any> = [...notificationsData.notifications];
      setNotifications([...arr.reverse()]);
      if(notificationsData.newNotification) {
        setTotalUnRead(notificationsData.notifications.filter((item: any) => item.read === "n").length)
      } else {
        setTotalUnRead(0)
      }
    }
  }, [notificationsData]);


  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleMarkAllAsRead = () => {
    triggerPutNotifications({}).then(res => {
      triggerGetNotifications();
    })
  };

  return (
    <>
      <IconButtonAnimate
        color={openPopover ? "primary" : "default"}
        onClick={handleOpenPopover}
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="eva:bell-fill" />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        sx={{ width: 360, p: 0 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notificações</Typography>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {totalUnRead > 0 ? (
                <>
                  Você tem {totalUnRead} nova{totalUnRead > 1 ? "s" : ""}{" "}
                  notificaç{totalUnRead > 1 ? "ões" : "ão"}.
                </>
              ) : (
                <>Você não tem novas notificações.</>
              )}
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title=" Marcar como lido">
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Scrollbar sx={{ height: { xs: 340, sm: "auto" }, maxHeight: "500px" }}>
          <List disablePadding>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
              />
            ))}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: "dashed" }} />

        {/* <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple>
            View All
          </Button>
        </Box> */}
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

type NotificationItemProps = {
  id: string;
  title: string;
  body: string;
  avatar: string | null;
  type: string;
  date: Date;
  read: "s" | "n";
};

function NotificationItem({
  notification,
}: {
  notification: NotificationItemProps;
}) {
  const { avatar, title } = renderContent(notification);

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: "1px",
        ...(notification.read === "n" && {
          bgcolor: "action.selected",
        }),
      }}
    >
      {/* <ListItemAvatar>
        <Avatar sx={{ bgcolor: "background.neutral" }}>N</Avatar>
      </ListItemAvatar> */}

      <ListItemText
        disableTypography
        primary={title}
        secondary={
          <Stack
            direction="row"
            sx={{ mt: 0.5, typography: "caption", color: "text.disabled" }}
          >
            <Iconify icon="eva:clock-fill" width={16} sx={{ mr: 0.5 }} />
            <Typography variant="caption">
              {fToNow(notification.date)}
            </Typography>
          </Stack>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification: NotificationItemProps) {
  const title = (
    <Typography variant="subtitle2">
      {notification.title}
      <Typography
        component="span"
        variant="body2"
        sx={{ color: "text.secondary" }}
      >
        &nbsp; {notification.body}
      </Typography>
    </Typography>
  );

  if (notification.type === "order_placed") {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="/assets/icons/notification/ic_package.svg"
        />
      ),
      title,
    };
  }
  if (notification.type === "order_shipped") {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="/assets/icons/notification/ic_shipping.svg"
        />
      ),
      title,
    };
  }
  if (notification.type === "mail") {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="/assets/icons/notification/ic_mail.svg"
        />
      ),
      title,
    };
  }
  if (notification.type === "chat_message") {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="/assets/icons/notification/ic_chat.svg"
        />
      ),
      title,
    };
  }
  return {
    avatar: notification.avatar ? (
      <img alt={notification.title} src={notification.avatar} />
    ) : null,
    title,
  };
}
